import { useEffect, useState, useMemo } from "react";
import Tree from "rc-tree";
import "rc-tree/assets/index.css";
import { Plus, FolderPlus, Trash2 } from "lucide-react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  getDesignFolderTree,
  createDesignFolder,
  uploadDesignDoc,
  getDesignDocDetail,
  // ✅ 설계도서 폴더 삭제 API (프로젝트 공유 자료실의 deletePapersFolder에 대응)
  deleteDesignFolder,
} from "../../api/design";

import DesignDocView from "../../components/design/DesignDocView";
import PageHeader from "../../components/common/ui/PageHeader";

export default function DesignLibrary() {
  const { id: projectId } = useParams();
  const [searchParams] = useSearchParams();
  const autoDocId = searchParams.get("doc");

  const [treeData, setTreeData] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [docDetail, setDocDetail] = useState(null);
  const [mode, setMode] = useState("view"); // view | upload | new-folder

  // 업로드 상태
  const [file, setFile] = useState(null);
  const [discipline, setDiscipline] = useState("ARCH");
  const [category, setCategory] = useState("도면");
  const [version, setVersion] = useState("v1.0");
  const [comment, setComment] = useState("");

  // 새 폴더 상태
  const [newFolderName, setNewFolderName] = useState("");

  const [expandedKeys, setExpandedKeys] = useState([]);

  // ----------------------
  // 선택한 폴더 아래에 문서(doc-*)가 있는지 여부 계산
  // ----------------------
  const findNodeByKey = (nodes, key) => {
    for (const node of nodes) {
      if (node.key === key) return node;
      if (node.children && node.children.length > 0) {
        const found = findNodeByKey(node.children, key);
        if (found) return found;
      }
    }
    return null;
  };

  const hasDocDescendant = (node) => {
    if (!node || !node.children) return false;
    for (const child of node.children) {
      // 설계도서는 문서를 doc-* 키로 구분
      if (typeof child.key === "string" && child.key.startsWith("doc-")) {
        return true;
      }
      // 하위 폴더도 재귀적으로 검사
      if (!child.isLeaf && hasDocDescendant(child)) {
        return true;
      }
    }
    return false;
  };

  // 선택된 폴더 아래에 하나라도 문서가 있으면 true
  const hasFilesInSelectedFolder = useMemo(() => {
    if (!selectedFolder) return false;
    const node = findNodeByKey(treeData, selectedFolder);
    return hasDocDescendant(node);
  }, [selectedFolder, treeData]);

  // 트리의 모든 노드 key 수집 → 전체 펼치기용
  const collectAllKeys = (nodes) => {
    const keys = [];
    const traverse = (list) => {
      list.forEach((node) => {
        keys.push(node.key);
        if (node.children && node.children.length > 0) {
          traverse(node.children);
        }
      });
    };
    traverse(nodes);
    return keys;
  };

  // ----------------------
  // 트리 불러오기
  // ----------------------
  const loadTree = async () => {
    try {
      const res = await getDesignFolderTree(projectId);

      const formatNode = (node) => ({
        key: node.isLeaf ? `doc-${node.id}` : `folder-${node.id}`,
        title: node.name,
        isLeaf: node.isLeaf || false,
        children: node.children ? node.children.map(formatNode) : [],
      });

      const formatted = res.data.map(formatNode);
      setTreeData(formatted);

      // 페이지 진입 시 전체 트리 펼치기
      setExpandedKeys(collectAllKeys(formatted));
    } catch (err) {
      console.error("폴더 트리 불러오기 실패:", err);
    }
  };

  useEffect(() => {
    loadTree();
  }, [projectId]);

  // ----------------------
  // 문서 선택 → 상세 불러오기
  // ----------------------
  const handleSelect = async (keys, info) => {
    const key = info.node.key;

    if (key.startsWith("doc-")) {
      const docId = key.replace("doc-", "");
      setSelectedDoc(info.node.title);
      setSelectedFolder(null);
      setMode("view");

      try {
        const res = await getDesignDocDetail(docId);
        setDocDetail(res.data);
      } catch (err) {
        console.error("문서 상세 불러오기 실패:", err);
      }
    } else if (key.startsWith("folder-")) {
      setSelectedFolder(key);
      setSelectedDoc(null);
      setDocDetail(null);
      setMode("view");
    }
  };

  useEffect(() => {
    if (!autoDocId) return;

    (async () => {
      try {
        const res = await getDesignDocDetail(autoDocId);
        setDocDetail(res.data);
        setSelectedDoc(res.data.name);
        setMode("view");
      } catch (e) {
        console.error("자동 문서 로드 실패", e);
      }
    })();
  }, [autoDocId]);


  // ----------------------
  // 업로드
  // ----------------------
  const handleUpload = async () => {
    if (!file || !selectedFolder) {
      alert("파일과 폴더를 선택하세요!");
      return;
    }
    const formData = new FormData();
    formData.append("project", projectId);
    formData.append("folder", selectedFolder.replace("folder-", ""));
    formData.append("name", file.name);
    formData.append("discipline", discipline);
    formData.append("category", category);
    formData.append("version", version);
    formData.append("file", file);
    formData.append("comment", comment);

    try {
      await uploadDesignDoc(formData);
      alert("업로드 성공!");
      setMode("view");
      setSelectedDoc(file.name);
      loadTree();
      if (selectedFolder) {
        setExpandedKeys((prev) => [...new Set([...prev, selectedFolder])]);
      }
    } catch (err) {
      console.error("업로드 실패:", err.response?.data || err);
      alert("업로드 실패");
    }
  };

  // ----------------------
  // 새 폴더 생성
  // ----------------------
  const handleCreateFolder = async () => {
    if (!newFolderName) {
      alert("폴더명을 입력하세요!");
      return;
    }
    try {
      await createDesignFolder({
        project: projectId,
        name: newFolderName,
        parent: selectedFolder ? selectedFolder.replace("folder-", "") : null,
      });
      alert("폴더 생성 완료!");
      setMode("view");
      setNewFolderName("");
      loadTree();

      setExpandedKeys((prev) => [...new Set([...prev, selectedFolder])]);
    } catch (err) {
      console.error("폴더 생성 실패:", err);
      alert("폴더 생성 실패");
    }
  };

  // ----------------------
  // 폴더 삭제
  // ----------------------
  const handleDeleteFolder = async () => {
    if (!selectedFolder) return;

    // 폴더 내에 문서가 있으면 삭제 불가
    if (hasFilesInSelectedFolder) {
      alert(
        "폴더 내 파일이 있는 경우 삭제할 수 없습니다.\n먼저 해당 폴더(및 하위 폴더)의 파일을 모두 삭제하세요."
      );
      return;
    }

    const id = Number(selectedFolder.replace("folder-", ""));
    if (!Number.isFinite(id)) return;

    if (!window.confirm("정말로 이 폴더를 삭제할까요?")) {
      return;
    }

    try {
      await deleteDesignFolder(id);
      setSelectedFolder(null);
      setSelectedDoc(null);
      setDocDetail(null);
      await loadTree();
      alert("폴더 삭제 완료!");
    } catch (err) {
      console.error("폴더 삭제 실패:", err);
      alert("폴더 삭제 실패");
    }
  };

  // ----------------------
  // 트리 노드 렌더링
  // ----------------------
  const renderTitle = (node) => (
    <span className="ml-1 text-sm">{node.title}</span>
  );

  return (
    <div className="p-6">
      <PageHeader>도서 라이브러리</PageHeader>
      <div className="grid grid-cols-3 gap-6 h-full p-6">
        {/* 좌측: 트리 */}
        <div className="col-span-1 rounded-xl border bg-white shadow-sm p-4 flex flex-col h-[700px] ">
          <div className="flex items-center justify-between mb-4">
            {/* 타이틀 */}
            <span className="flex items-center gap-2 text-base font-semibold text-slate-800">
              📂 설계도서
            </span>

            {/* 새 폴더 / 폴더 삭제 버튼 (프로젝트 공유 자료실과 동일 위치) */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setMode("new-folder")}
                className="flex items-center gap-1 rounded-md border border-slate-300 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-100 hover:text-slate-900 transition"
              >
                <FolderPlus className="w-4 h-4" />
                <span>새 폴더</span>
              </button>
              <button
                onClick={handleDeleteFolder}
                className="flex items-center gap-1 rounded-md border border-rose-300 px-3 py-1.5 text-sm text-rose-700 hover:bg-rose-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!selectedFolder || hasFilesInSelectedFolder}
                title={
                  !selectedFolder
                    ? "삭제할 폴더를 먼저 선택하세요"
                    : hasFilesInSelectedFolder
                      ? "폴더 내 파일이 있어 삭제할 수 없습니다. 파일을 모두 삭제하세요."
                      : ""
                }
              >
                <Trash2 className="w-4 h-4" />
                <span>폴더 삭제</span>
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-auto">
            <Tree
              treeData={treeData}
              expandedKeys={expandedKeys}
              onExpand={setExpandedKeys}
              // 📁 폴더 텍스트 클릭 시 열고/닫기 (자료실 선택 모달과 동일)
              expandAction="click"
              titleRender={renderTitle}
              className="text-slate-700"
              onSelect={handleSelect}
            />
          </div>

          <div className="flex gap-2 mt-4">
            {/* <button
            onClick={() => setMode("upload")}
            className="flex-1 flex items-center justify-center gap-2 rounded-lg bg-indigo-600 px-3 py-2 text-white shadow hover:bg-indigo-700 transition"
          >
            <Plus className="w-4 h-4" /> <span>업로드</span>
          </button> */}
          </div>
        </div>

        {/* 우측: 상세/업로드/폴더 생성 */}
        <div className="col-span-2">
          <div className="rounded-xl border bg-white shadow-sm p-6 h-full">
            {/* 문서 상세 */}
            {mode === "view" && docDetail && (
              <DesignDocView docDetail={docDetail} />
            )}

            {/* 업로드 */}
            {mode === "upload" && (
              <div className="space-y-4">
                <span className="text-base font-semibold text-slate-800">
                  새 문서 업로드
                </span>
                <input
                  type="file"
                  onChange={(e) => setFile(e.target.files[0])}
                  className="block w-full text-sm text-slate-600 file:mr-4 file:rounded-lg file:border-0 file:bg-indigo-50 file:px-4 file:py-2 file:text-indigo-700 hover:file:bg-indigo-100"
                />
                <select
                  value={discipline}
                  onChange={(e) => setDiscipline(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                >
                  <option value="ARCH">건축</option>
                  <option value="STRU">구조</option>
                  <option value="ELEC">전기</option>
                  <option value="MECH">설비</option>
                  <option value="CIVIL">토목</option>
                  <option value="ETC">기타</option>
                </select>
                <input
                  type="text"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  placeholder="문서 유형"
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                />
                <input
                  type="text"
                  value={version}
                  onChange={(e) => setVersion(e.target.value)}
                  placeholder="버전 (예: v1.0)"
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                />
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="업로드 코멘트"
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  rows={3}
                />
                <button
                  onClick={handleUpload}
                  className="w-full rounded-lg bg-indigo-600 px-4 py-2 text-white shadow hover:bg-indigo-700 transition"
                >
                  업로드
                </button>
              </div>
            )}

            {/* 새 폴더 */}
            {mode === "new-folder" && (
              <div className="space-y-4">
                <span className="text-base font-semibold text-slate-800">
                  새 폴더 생성
                </span>
                <input
                  type="text"
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  placeholder="폴더명"
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                />
                <button
                  onClick={handleCreateFolder}
                  className="w-full rounded-lg bg-slate-600 px-4 py-2 text-white shadow hover:bg-slate-700 transition"
                >
                  폴더 생성
                </button>
              </div>
            )}

            {!selectedDoc && mode === "view" && !docDetail && (
              <div className="flex flex-col items-center gap-3 text-center">
                <p className="text-slate-500">
                  좌측에서 문서를 선택하거나 새 문서를 업로드하세요.
                </p>
                <button
                  onClick={() => setMode("upload")}
                  className="flex items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-white shadow hover:bg-indigo-700 transition"
                >
                  <Plus className="w-4 h-4" /> <span>업로드</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
